// Typisierter API-Client. Konsumiert ausschließlich die geteilten Contract-Typen aus @wf/shared —
// eine Backend-Vertragsänderung wird damit zum TYP-Fehler hier, nie zu stiller Drift.
import type {
  AdminAuditResponse,
  AdminCreateUserRequest,
  AdminCreateUserResponse,
  AdminPatchUserRequest,
  AdminStatsResponse,
  AuditResponse,
  AdminWorkshopDetailResponse,
  AdminWorkshopsResponse,
  CreateWorkshopRequest,
  CreateWorkshopResponse,
  DeleteWorkshopResponse,
  ResetChefPasswordRequest,
  ResetChefPasswordResponse,
  UpdateWorkshopRequest,
  UpdateWorkshopResponse,
  ChangePasswordRequest,
  ChangeNameRequest,
  ChangeUsernameRequest,
  EmailVerifyChallengeResponse,
  RegisterRequest,
  RequestResetRequest,
  ResetPasswordRequest,
  ResendVerificationRequest,
  SetEmailRequest,
  VerifyEmailRequest,
  VerifyOwnEmailRequest,
  CreateOnboardCodeRequest,
  UpdateUserRequest,
  CreateOnboardCodeResponse,
  CreateResetCodeRequest,
  CreateResetCodeResponse,
  RedeemOnboardRequest,
  RedeemResetCodeRequest,
  UsersListResponse,
  TwofaSetupResponse,
  TwofaEnableRequest,
  TwofaEnableResponse,
  TwofaDisableRequest,
  AddItemRequest,
  AddPartRequest,
  AddTaskRequest,
  AddTimeRequest,
  AddTimeResponse,
  AbsenceDTO,
  AbsenceTypesResponse,
  AbsencesResponse,
  CancelAbsenceRequest,
  CreateAbsenceRequest,
  DecideAbsenceRequest,
  VacationAccountResponse,
  VacationAssistantResponse,
  VacationNoticeDTO,
  SendVacationNoticeRequest,
  ForfeitVacationRequest,
  ArbzgAckRequest,
  CloseTimeAccountRequest,
  ReopenTimeAccountRequest,
  TimeAccountPeriodDTO,
  TimeAccountResponse,
  MarkNotificationsReadRequest,
  MarkNotificationsReadResponse,
  NotificationsResponse,
  VapidKeyResponse,
  PushSubscribeRequest,
  PushUnsubscribeRequest,
  AttendanceReportResponse,
  AttendanceState,
  AttendanceTodayResponse,
  AdjustStockRequest,
  AdvanceProcurementRequest,
  CorrectAttendanceRequest,
  CorrectionsResponse,
  CreateCorrectionRequest,
  CreateCustomerRequest,
  CreateInventoryPartRequest,
  CreateOrderRequest,
  CreateRestockRequest,
  CreateServiceRequest,
  CreateVehicleRequest,
  CriticalPartsResponse,
  CustomerDetailResponse,
  CustomerExportResponse,
  CustomerResponse,
  CustomersListResponse,
  InventoryListResponse,
  InventoryPartArchivedResponse,
  InventoryPartResponse,
  InvoiceDocResponse,
  InvoiceResponse,
  InvoicesResponse,
  PatchInventoryPartRequest,
  ProcurementItemResponse,
  ProcurementListResponse,
  ReactivateInventoryPartRequest,
  SnrCheckResponse,
  StockMoveRequest,
  LoginChallengeResponse,
  LoginRequest,
  LoginResponse,
  OpenItemsResponse,
  OrderChildResponse,
  OrderDetailResponse,
  OrderResponse,
  OrdersResponse,
  OrderTimeResponse,
  PartLookupResponse,
  PartsListResponse,
  PublicEstimateDecisionResponse,
  PublicEstimateResponse,
  PatchCustomerRequest,
  PatchItemRequest,
  PatchOrderRequest,
  PatchOrderResponse,
  PatchPartRequest,
  PatchServiceRequest,
  PatchSettingsRequest,
  PatchTaskRequest,
  PatchVehicleRequest,
  ResolveCorrectionRequest,
  SendEstimateResponse,
  ServiceResponse,
  ServicesResponse,
  SetWorkPlanRequest,
  SettingsResponse,
  StampConvertedResponse,
  StampRequest,
  StatusResponse,
  TimesheetResponse,
  ArbZgAverageResponse,
  VehicleHistoryResponse,
  VehicleResponse,
  WorkPlanResponse,
  AbsenceSeriesListResponse,
  CreateAbsenceSeriesRequest,
  CreateAbsenceSeriesResponse,
  CancelAbsenceSeriesRequest,
} from '@wf/shared'
import type {
  AccessLevel,
  BillingInterval,
  BillingStatus,
  BillingStatusResponse as BillingStatusFull,
  ChangePlanRequest,
  PlanTier,
} from '@wf/shared'
import type { AboInvoiceDocResponse, AboInvoiceListResponse } from '@wf/shared'
import { impersonationHeaders } from '../app/impersonation'

/** Login-Antwort: Session (`user`), 2FA-Aufforderung (`needTotp`) oder E-Mail-Verifizierungs-Aufforderung. */
export type LoginResult = LoginResponse | LoginChallengeResponse | EmailVerifyChallengeResponse

// Abo/Billing (W4-Frontend). Die Backend-Lane hält den Vertrag in apps/backend/src/handlers/billing.ts
// (statusPayload); solange @wf/shared die Status-Form NICHT additiv spiegelt (Handoff §5), lebt der Typ hier.
// Geld immer in ganzen Cent; Anzeige-Headline = die `…Gross`-Felder (der Kunde zahlt brutto).

/** Bestandswerkstatt ohne Abo-Kopf: `GET /billing/status` liefert nur diese Kurzform. */
export interface BillingStatusUnprovisioned {
  provisioned: false
  accessLevel: 'full'
  seatsUsed: number
}

/**
 * Voller Abo-Status (`GET /billing/status` bzw. Antwort von cancel/change-plan) — Tarif-Modell. Der Vertrag lebt
 * jetzt in @wf/shared (BillingStatusResponse: planTier, billingInterval, priceCentsNet+Gross, userLimit, die
 * scheduled-Felder, availablePlans); hier nur das Discriminated-Union-Flag `provisioned: true` aufgesetzt.
 */
export type BillingStatusProvisioned = BillingStatusFull & { provisioned: true }

export type BillingStatusResponse = BillingStatusUnprovisioned | BillingStatusProvisioned

/** `POST /billing/checkout` → gehostete Anbieter-URL, auf die das Frontend weiterleitet. */
export interface BillingCheckoutResponse {
  redirectUrl: string
}

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown
  constructor(status: number, message: string, body: unknown) {
    super(message)
    this.status = status
    this.body = body
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    credentials: 'include', // HttpOnly-Session-Cookie
    // Support-Modus (Betreiber): X-Impersonate-Workshop-Id an JEDEM Request — der Server akzeptiert ihn
    // nur vom Superadmin und auditiert jede Mutation (auth.resolveImpersonation).
    headers: { 'content-type': 'application/json', ...impersonationHeaders(), ...(init?.headers ?? {}) },
    ...init,
  })
  const text = await res.text()
  const body: unknown = text ? JSON.parse(text) : null
  if (!res.ok) {
    const message =
      body && typeof body === 'object' && 'error' in body ? String((body as { error: unknown }).error) : `Fehler ${res.status}`
    throw new ApiError(res.status, message, body)
  }
  return body as T
}

export const api = {
  status: () => request<StatusResponse>('status'),
  login: (req: LoginRequest) => request<LoginResult>('login', { method: 'POST', body: JSON.stringify(req) }),
  logout: () => request<{ ok: true }>('logout', { method: 'POST', body: '{}' }),
  // Werkstatt-Selbstregistrierung (Admin/Chef): legt Werkstatt + Chef an; KEINE Session — erst E-Mail verifizieren.
  register: (body: RegisterRequest) => request<EmailVerifyChallengeResponse>('register', { method: 'POST', body: JSON.stringify(body) }),
  // E-Mail-Pflicht-Verifizierung (öffentlich): Code einlösen → eingeloggt; bzw. Code erneut anfordern.
  verifyEmail: (body: VerifyEmailRequest) => request<LoginResponse>('verify-email', { method: 'POST', body: JSON.stringify(body) }),
  resendVerification: (body: ResendVerificationRequest) => request<{ ok: true }>('verify-email/resend', { method: 'POST', body: JSON.stringify(body) }),
  // Eigene E-Mail (eingeloggt) hinzufügen/ändern (sendet Code) + per Code verifizieren.
  setEmail: (body: SetEmailRequest) => request<{ ok: true }>('me/email', { method: 'POST', body: JSON.stringify(body) }),
  verifyOwnEmail: (body: VerifyOwnEmailRequest) => request<{ ok: true }>('me/email/verify', { method: 'POST', body: JSON.stringify(body) }),
  // E-Mail-Passwort-Reset (für Admins / Nutzer mit hinterlegter E-Mail). request-reset antwortet IMMER 200
  // (keine Existenz-Auskunft); reset-password löst den per E-Mail erhaltenen Token ein.
  requestReset: (body: RequestResetRequest) => request<{ ok: true }>('request-reset', { method: 'POST', body: JSON.stringify(body) }),
  resetPassword: (body: ResetPasswordRequest) => request<{ ok: true }>('reset-password', { method: 'POST', body: JSON.stringify(body) }),
  // ---- Onboarding per Einmal-ID ----
  // Öffentlich (kein Login): Mitarbeiter löst Onboarding-Code ein (Auto-Login) bzw. Reset-Code ein.
  redeemOnboard: (body: RedeemOnboardRequest) =>
    request<LoginResponse>('onboarding/register', { method: 'POST', body: JSON.stringify(body) }),
  redeemResetCode: (body: RedeemResetCodeRequest) =>
    request<{ ok: true }>('onboarding/reset', { method: 'POST', body: JSON.stringify(body) }),
  // Chef (cap team): Team-Liste + Einmal-Codes erzeugen (Klartext-Code genau einmal in der Antwort).
  listUsers: () => request<UsersListResponse>('users'),
  updateUser: (id: number, body: UpdateUserRequest) =>
    request<{ ok: true }>(`users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  createOnboardCode: (body: CreateOnboardCodeRequest) =>
    request<CreateOnboardCodeResponse>('onboarding/codes', { method: 'POST', body: JSON.stringify(body) }),
  createResetCode: (body: CreateResetCodeRequest) =>
    request<CreateResetCodeResponse>('onboarding/reset-codes', { method: 'POST', body: JSON.stringify(body) }),
  listOrders: () => request<OrdersResponse>('orders'),
  createOrder: (body: CreateOrderRequest) => request<OrderResponse>('orders', { method: 'POST', body: JSON.stringify(body) }),
  listArchive: () => request<OrdersResponse>('orders/archive'),
  listOpenItems: () => request<OpenItemsResponse>('orders/open'),
  listInvoices: () => request<InvoicesResponse>('orders/invoices'),
  getSettings: () => request<SettingsResponse>('settings'),
  patchSettings: (body: PatchSettingsRequest) =>
    request<SettingsResponse>('settings', { method: 'PATCH', body: JSON.stringify(body) }),
  getOrder: (id: number) => request<OrderDetailResponse>(`orders/${id}`),
  patchOrder: (id: number, body: PatchOrderRequest) =>
    request<PatchOrderResponse>(`orders/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Auftrag hart löschen (Recht auftrag_loeschen; der Server erzwingt es zusätzlich). Antwort {ok:true}.
  deleteOrder: (id: number) => request<{ ok: true }>(`orders/${id}`, { method: 'DELETE' }),
  // Auftrags-Kinder (Aufgaben). Jede Mutation antwortet mit dem frischen OrderDetailResponse.
  addTask: (orderId: number, body: AddTaskRequest) =>
    request<OrderChildResponse>(`orders/${orderId}/tasks`, { method: 'POST', body: JSON.stringify(body) }),
  patchTask: (taskId: number, body: PatchTaskRequest) =>
    request<OrderChildResponse>(`tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTask: (taskId: number) => request<OrderChildResponse>(`tasks/${taskId}`, { method: 'DELETE' }),
  // Auftrags-Kinder (Teile + Arbeits-Positionen). Antwort jeweils das frische OrderDetailResponse.
  addPart: (orderId: number, body: AddPartRequest) =>
    request<OrderChildResponse>(`orders/${orderId}/parts`, { method: 'POST', body: JSON.stringify(body) }),
  patchPart: (partId: number, body: PatchPartRequest) =>
    request<OrderChildResponse>(`parts/${partId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deletePart: (partId: number) => request<OrderChildResponse>(`parts/${partId}`, { method: 'DELETE' }),
  addItem: (orderId: number, body: AddItemRequest) =>
    request<OrderChildResponse>(`orders/${orderId}/items`, { method: 'POST', body: JSON.stringify(body) }),
  patchItem: (itemId: number, body: PatchItemRequest) =>
    request<OrderChildResponse>(`items/${itemId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteItem: (itemId: number) => request<OrderChildResponse>(`items/${itemId}`, { method: 'DELETE' }),
  // Rechnung festschreiben: friert den GoBD-Beleg-Snapshot ein + vergibt die §14-Nummer (idempotent — `already`).
  createInvoice: (orderId: number) => request<InvoiceResponse>(`orders/${orderId}/invoice`, { method: 'POST', body: '{}' }),
  // Rechnung stornieren: legt eine Stornorechnung an (append-only) und öffnet den Auftrag zur Korrektur.
  stornoInvoice: (orderId: number) => request<InvoiceResponse>(`orders/${orderId}/storno`, { method: 'POST', body: '{}' }),
  // Aktiver, eingefrorener Beleg eines Auftrags (finale Rechnungsansicht) — 404, wenn noch nichts festgeschrieben.
  getOrderInvoice: (orderId: number) => request<InvoiceDocResponse>(`orders/${orderId}/invoice`),
  // Einzelnen Beleg (Rechnung ODER Stornorechnung) aus dem Ledger laden.
  getInvoice: (invoiceId: number) => request<InvoiceDocResponse>(`invoices/${invoiceId}`),
  // Kostenvoranschlag an den Kunden senden: setzt estimateStatus 'sent' und gibt EINMALIG den Klartext-
  // Freigabe-Token zurück (die DB speichert nur den Hash) — daraus baut die UI den öffentlichen Freigabe-Link.
  sendEstimate: (orderId: number) =>
    request<SendEstimateResponse>(`orders/${orderId}/estimate`, { method: 'POST', body: '{}' }),
  // Öffentliche KV-Freigabe (ohne Login): der Kunde öffnet den Link, sieht den reduzierten KV und entscheidet.
  getPublicEstimate: (token: string) => request<PublicEstimateResponse>(`public/estimate/${token}`),
  approveEstimate: (token: string) =>
    request<PublicEstimateDecisionResponse>(`public/estimate/${token}/approve`, { method: 'POST', body: '{}' }),
  declineEstimate: (token: string) =>
    request<PublicEstimateDecisionResponse>(`public/estimate/${token}/decline`, { method: 'POST', body: '{}' }),
  listOrderTime: (orderId: number) => request<OrderTimeResponse>(`orders/${orderId}/time`),
  addTime: (orderId: number, body: AddTimeRequest) =>
    request<AddTimeResponse>(`orders/${orderId}/time`, { method: 'POST', body: JSON.stringify(body) }),
  // Urlaubskonto (2a): eigenes Konto bzw. fremdes mit Arbeitszeit-Leserecht.
  vacation: (userId?: number, year?: number) => {
    const q = new URLSearchParams()
    if (userId != null) q.set('userId', String(userId))
    if (year != null) q.set('year', String(year))
    const qs = q.toString()
    return request<VacationAccountResponse>(qs ? `vacation?${qs}` : 'vacation')
  },
  vacationAssistant: (year?: number) =>
    request<VacationAssistantResponse>(year != null ? `vacation/assistant?year=${year}` : 'vacation/assistant'),
  sendVacationNotice: (body: SendVacationNoticeRequest) =>
    request<{ notice: VacationNoticeDTO }>('vacation/notice', { method: 'POST', body: JSON.stringify(body) }),
  ackVacationNotice: (id: number) => request<{ notice: VacationNoticeDTO }>(`vacation/notice/${id}/ack`, { method: 'POST', body: '{}' }),
  forfeitVacation: (body: ForfeitVacationRequest) =>
    request<{ ok: boolean }>('vacation/forfeit', { method: 'POST', body: JSON.stringify(body) }),
  // Abwesenheiten (Phase 2): Typen fürs Formular, eigene Liste / Team-Sicht, Antrag/Entscheidung/Storno.
  absenceTypes: () => request<AbsenceTypesResponse>('absence-types'),
  listAbsences: (scope: 'me' | 'team', month?: string) =>
    request<AbsencesResponse>(scope === 'team' ? `absences?scope=team${month ? `&month=${month}` : ''}` : 'absences'),
  createAbsence: (body: CreateAbsenceRequest) => request<{ absence: AbsenceDTO }>('absences', { method: 'POST', body: JSON.stringify(body) }),
  decideAbsence: (id: number, body: DecideAbsenceRequest) =>
    request<{ absence: AbsenceDTO }>(`absences/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  cancelAbsence: (id: number, body: CancelAbsenceRequest) =>
    request<{ absence: AbsenceDTO }>(`absences/${id}/cancel`, { method: 'POST', body: JSON.stringify(body) }),
  // Serien (Azubi-Block): wiederkehrender Schultag als EIN Vorgang statt ~40 Einzeleintraegen.
  listAbsenceSeries: (userId?: number) =>
    request<AbsenceSeriesListResponse>(userId != null ? `absence-series?user_id=${userId}` : 'absence-series'),
  createAbsenceSeries: (body: CreateAbsenceSeriesRequest) =>
    request<CreateAbsenceSeriesResponse>('absence-series', { method: 'POST', body: JSON.stringify(body) }),
  cancelAbsenceSeries: (id: number, body: CancelAbsenceSeriesRequest) =>
    request<{ cancelled: number; frozen: number }>(`absence-series/${id}/cancel`, { method: 'POST', body: JSON.stringify(body) }),
  listCustomers: () => request<CustomersListResponse>('customers'),
  getCustomer: (id: number) => request<CustomerDetailResponse>(`customers/${id}`),
  createCustomer: (body: CreateCustomerRequest) =>
    request<CustomerResponse>('customers', { method: 'POST', body: JSON.stringify(body) }),
  patchCustomer: (id: number, body: PatchCustomerRequest) =>
    request<CustomerResponse>(`customers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // DSGVO am Kunden: Datenauskunft exportieren, anonymisieren (Löschpfad ohne Datenverlust), oder hart
  // löschen — Letzteres antwortet 409 {blocked:'has_orders'}, wenn Aufträge bestehen (GoBD-Aufbewahrung).
  exportCustomer: (id: number) => request<CustomerExportResponse>(`customers/${id}/export`),
  anonymizeCustomer: (id: number) => request<CustomerResponse>(`customers/${id}/anonymize`, { method: 'POST', body: '{}' }),
  deleteCustomer: (id: number) => request<{ ok: true }>(`customers/${id}`, { method: 'DELETE' }),
  patchVehicle: (vehicleId: number, body: PatchVehicleRequest) =>
    request<VehicleResponse>(`vehicles/${vehicleId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Fahrzeug anlegen/löschen/anonymisieren + Fahrzeug-Historie (Aufträge dieses Fahrzeugs). Toter, fertiger
  // Backend-Bestand, im UI nachgezogen. Anonymisieren = DSGVO-Pfad ohne Datenverlust (Aufträge bleiben).
  createVehicle: (body: CreateVehicleRequest) =>
    request<VehicleResponse>('vehicles', { method: 'POST', body: JSON.stringify(body) }),
  deleteVehicle: (vehicleId: number) => request<{ ok: true }>(`vehicles/${vehicleId}`, { method: 'DELETE' }),
  anonymizeVehicle: (vehicleId: number) =>
    request<VehicleResponse>(`vehicles/${vehicleId}/anonymize`, { method: 'POST', body: '{}' }),
  vehicleHistory: (vehicleId: number) => request<VehicleHistoryResponse>(`vehicles/${vehicleId}/history`),
  // Werkstattweite Teile-Bestellpipeline (Einkauf): alle Teile über alle Aufträge mit Auftrags-/Fahrzeug-Kontext.
  listWorkshopParts: () => request<PartsListResponse>('parts'),
  // Teile-Gedächtnis: letzter Treffer einer Teilenummer in der Werkstatt (für Scan/Nummern-Suche → Vorbefüllen).
  partLookup: (num: string) => request<PartLookupResponse>(`parts/lookup?num=${encodeURIComponent(num)}`),
  // ---- Inventar (Lager) ----
  listInventory: () => request<InventoryListResponse>('inventory'),
  criticalParts: () => request<CriticalPartsResponse>('inventory/critical'),
  archivedInventory: () => request<InventoryListResponse>('inventory/archived'),
  searchInventory: (q: string) => request<InventoryListResponse>(`inventory/search?q=${encodeURIComponent(q)}`),
  checkSnr: (num: string) => request<SnrCheckResponse>(`inventory/check?num=${encodeURIComponent(num)}`),
  getInventoryPart: (id: number) => request<InventoryPartResponse>(`inventory/${id}`),
  // Anlegen: 201 { part } neu, ODER 200 { archived:true, part } (SNR gehört archiviertem Teil → reaktivieren anbieten).
  createInventoryPart: (body: CreateInventoryPartRequest) =>
    request<InventoryPartResponse | InventoryPartArchivedResponse>('inventory', { method: 'POST', body: JSON.stringify(body) }),
  patchInventoryPart: (id: number, body: PatchInventoryPartRequest) =>
    request<InventoryPartResponse>(`inventory/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  reactivateInventoryPart: (id: number, body: ReactivateInventoryPartRequest) =>
    request<InventoryPartResponse>(`inventory/${id}/reactivate`, { method: 'POST', body: JSON.stringify(body) }),
  archiveInventoryPart: (id: number) =>
    request<InventoryPartResponse>(`inventory/${id}/archive`, { method: 'POST', body: '{}' }),
  adjustStock: (id: number, body: AdjustStockRequest) =>
    request<InventoryPartResponse>(`inventory/${id}/adjust`, { method: 'POST', body: JSON.stringify(body) }),
  withdrawStock: (id: number, body: StockMoveRequest) =>
    request<InventoryPartResponse>(`inventory/${id}/withdraw`, { method: 'POST', body: JSON.stringify(body) }),
  addStock: (id: number, body: StockMoveRequest) =>
    request<InventoryPartResponse>(`inventory/${id}/add`, { method: 'POST', body: JSON.stringify(body) }),
  // ---- Beschaffung (Einkauf-Board) ----
  listProcurement: () => request<ProcurementListResponse>('procurement'),
  createRestock: (body: CreateRestockRequest) =>
    request<ProcurementItemResponse>('procurement/restock', { method: 'POST', body: JSON.stringify(body) }),
  advanceProcurement: (id: number, body: AdvanceProcurementRequest) =>
    request<ProcurementItemResponse>(`procurement/${id}/advance`, { method: 'POST', body: JSON.stringify(body) }),
  // Preiskatalog (Standard-Leistungen mit Festpreis). GET ist für alle Eingeloggten (Picker auf dem Auftrag);
  // Schreiben erfordert serverseitig das Preise-Recht (canManagePrices).
  listServices: () => request<ServicesResponse>('services'),
  createService: (body: CreateServiceRequest) =>
    request<ServiceResponse>('services', { method: 'POST', body: JSON.stringify(body) }),
  patchService: (id: number, body: PatchServiceRequest) =>
    request<ServiceResponse>(`services/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteService: (id: number) => request<{ ok: true }>(`services/${id}`, { method: 'DELETE' }),
  attendanceMe: () => request<AttendanceState>('attendance?scope=me'),
  // Konsument: Team-Übersicht (Planung/Übersicht), Folge-Welle — Endpunkt + Guard sind bereits getestet.
  attendanceToday: () => request<AttendanceTodayResponse>('attendance?scope=today'),
  // 201 → AttendanceState (Stempel gebucht); 202 → StampConvertedResponse (in einen Auto-Korrekturantrag
  // umgewandelt: zu alt / kippt den Bestand). Beide sind 2xx; der Aufrufer diskriminiert am Body (R2/1.4).
  stamp: (body: StampRequest) =>
    request<AttendanceState | StampConvertedResponse>('attendance', { method: 'POST', body: JSON.stringify(body) }),
  // Mitarbeiter meldet dem Chef eine Zeit-Korrektur (vergessener/falscher Stempel). Quittung {ok:true};
  // die Liste/Auflösung der Anfragen ist Chef-Sache (eigene Welle), hier nur das Einreichen.
  createCorrection: (body: CreateCorrectionRequest) =>
    request<{ ok: true }>('corrections', { method: 'POST', body: JSON.stringify(body) }),
  // ---- Zeitmanagement (W9, „Zeiten"-Tab) ----
  // Zeitnachweis (Tageszeilen + Roh-Stempel inkl. entwerteter): eigener für jeden, fremde nur Chef/Zweitchef.
  attendanceReport: (from: string, to: string, userId?: number) =>
    request<AttendanceReportResponse>(
      `attendance/report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${userId != null ? `&userId=${userId}` : ''}`,
    ),
  // Chef-Korrektur (revisionssicher): Stempel entwerten oder nachtragen — Pflicht-Grund, isBoss.
  correctAttendance: (body: CorrectAttendanceRequest) =>
    request<{ ok: true }>('attendance/correct', { method: 'POST', body: JSON.stringify(body) }),
  // ArbZG-Verstoß quittieren (R4/1.10) — Recht arbeitszeit:bearbeiten.
  arbzgAck: (body: ArbzgAckRequest) => request<{ ok: true }>('attendance/arbzg-ack', { method: 'POST', body: JSON.stringify(body) }),
  // Zeitkonto/Monatsabschluss (Phase-1/2): Konto lesen + Monat hart abschließen / binnen 24 h zurücknehmen.
  timeAccount: (userId?: number) => request<TimeAccountResponse>(`timesheet/account${userId != null ? `?userId=${userId}` : ''}`),
  closeTimeAccount: (body: CloseTimeAccountRequest) =>
    request<{ ok: true; period: TimeAccountPeriodDTO }>('timesheet/close', { method: 'POST', body: JSON.stringify(body) }),
  reopenTimeAccount: (body: ReopenTimeAccountRequest) =>
    request<{ ok: true }>('timesheet/reopen', { method: 'POST', body: JSON.stringify(body) }),
  // Benachrichtigungen (In-App-Zentrale): eigene Liste + Ungelesen-Zähler; als gelesen markieren (idempotent).
  notifications: () => request<NotificationsResponse>('notifications'),
  markNotificationsRead: (body: MarkNotificationsReadRequest) =>
    request<MarkNotificationsReadResponse>('notifications/read', { method: 'POST', body: JSON.stringify(body) }),
  // OS-Push: öffentlichen VAPID-Key holen + Gerät an-/abmelden.
  vapidPublicKey: () => request<VapidKeyResponse>('push/vapid-public-key'),
  pushSubscribe: (body: PushSubscribeRequest) => request<{ ok: true }>('push/subscribe', { method: 'POST', body: JSON.stringify(body) }),
  pushUnsubscribe: (body: PushUnsubscribeRequest) => request<{ ok: true }>('push/unsubscribe', { method: 'POST', body: JSON.stringify(body) }),
  // Korrektur-Posteingang des Chefs (offene zuerst) + Anfrage abschließen (erledigt/abgelehnt).
  listCorrections: () => request<CorrectionsResponse>('corrections'),
  resolveCorrection: (id: number, body: ResolveCorrectionRequest) =>
    request<{ ok: true }>(`corrections/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  // Soll/Ist je Kalenderwoche (Dienstplan vs. Stempel): eigener für jeden, fremde nur Chef/Zweitchef.
  timesheet: (from: string, to: string, userId?: number) =>
    request<TimesheetResponse>(
      `timesheet?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${userId != null ? `&userId=${userId}` : ''}`,
    ),
  // § 3 ArbZG-Durchschnitt über das rollierende Ausgleichsfenster: eigener für jeden, fremde nur der
  // Freigabe-Kreis (der Fensteranfang kann ein Geburtsdatum verraten — der Server erzwingt es).
  arbzgAverage: (userId?: number) =>
    request<ArbZgAverageResponse>(`timesheet/durchschnitt${userId != null ? `?userId=${userId}` : ''}`),
  // Dienstplan (Wochenmuster Mo–Sa): eigener für jeden, fremde nur mit Dienstplan-Recht.
  // (GET /stats bleibt Backend-Bestand für das Übersicht-Dashboard — Client-Methode kommt mit der Welle, die sie nutzt.)
  getWorkPlan: (userId: number) => request<WorkPlanResponse>(`workplans/${userId}`),
  setWorkPlan: (body: SetWorkPlanRequest) =>
    request<{ ok: true }>('workplans', { method: 'PUT', body: JSON.stringify(body) }),
  // ---- Audit-Log-Viewer (Nachweiskette): Chef-Sicht (Recht audit:lesen) + Betreiber-Globalsicht ----
  audit: (from: string, to: string, action?: string) =>
    request<AuditResponse>(
      `audit?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${action ? `&action=${encodeURIComponent(action)}` : ''}`,
    ),
  adminAudit: (from: string, to: string, workshopId?: number) =>
    request<AdminAuditResponse>(
      `admin/audit?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${workshopId != null ? `&workshopId=${workshopId}` : ''}`,
    ),
  // ---- Betreiber-/Super-Admin: App-Verwaltung (nur is_superadmin; der Server erzwingt es zusätzlich) ----
  adminStats: () => request<AdminStatsResponse>('admin/stats'),
  adminListWorkshops: () => request<AdminWorkshopsResponse>('admin/workshops'),
  adminGetWorkshop: (id: number) => request<AdminWorkshopDetailResponse>(`admin/workshops/${id}`),
  adminCreateWorkshop: (body: CreateWorkshopRequest) =>
    request<CreateWorkshopResponse>('admin/workshops', { method: 'POST', body: JSON.stringify(body) }),
  adminUpdateWorkshop: (id: number, body: UpdateWorkshopRequest) =>
    request<UpdateWorkshopResponse>(`admin/workshops/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminDeleteWorkshop: (id: number) => request<DeleteWorkshopResponse>(`admin/workshops/${id}`, { method: 'DELETE' }),
  adminResetChefPassword: (id: number, body: ResetChefPasswordRequest) =>
    request<ResetChefPasswordResponse>(`admin/workshops/${id}/reset-chef-password`, { method: 'POST', body: JSON.stringify(body) }),
  // Mitarbeiter einer (fremden) Werkstatt verwalten (Super-Admin):
  adminCreateUser: (workshopId: number, body: AdminCreateUserRequest) =>
    request<AdminCreateUserResponse>(`admin/workshops/${workshopId}/users`, { method: 'POST', body: JSON.stringify(body) }),
  adminPatchUser: (userId: number, body: AdminPatchUserRequest) =>
    request<{ ok: true }>(`admin/users/${userId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  adminDeleteUser: (userId: number) => request<{ ok: true }>(`admin/users/${userId}`, { method: 'DELETE' }),
  // ---- Mein Konto (Selbstservice: eigene Stammdaten + Passwort + 2FA-Enrollment) ----
  changeOwnName: (body: ChangeNameRequest) => request<{ ok: true }>('me/name', { method: 'POST', body: JSON.stringify(body) }),
  changeOwnUsername: (body: ChangeUsernameRequest) => request<{ ok: true }>('me/username', { method: 'POST', body: JSON.stringify(body) }),
  changeOwnPassword: (body: ChangePasswordRequest) =>
    request<{ ok: true }>('me/password', { method: 'POST', body: JSON.stringify(body) }),
  twofaSetup: () => request<TwofaSetupResponse>('me/2fa/setup', { method: 'POST', body: '{}' }),
  twofaEnable: (body: TwofaEnableRequest) => request<TwofaEnableResponse>('me/2fa/enable', { method: 'POST', body: JSON.stringify(body) }),
  twofaDisable: (body: TwofaDisableRequest) => request<{ ok: true }>('me/2fa/disable', { method: 'POST', body: JSON.stringify(body) }),
  // ---- Abo & Lizenzen (nur Chef: werkstatt_einstellungen-Schreibrecht; sonst 403) ----
  billingStatus: () => request<BillingStatusResponse>('billing/status'),
  // SEPA-Mandat einrichten → gehostete Redirect-URL (Frontend leitet dorthin). Optional: Tarif + Intervall mitwählen.
  billingCheckout: (body: { planTier?: PlanTier; billingInterval?: BillingInterval } = {}) =>
    request<BillingCheckoutResponse>('billing/checkout', { method: 'POST', body: JSON.stringify(body) }),
  // Tarif wechseln (Upgrade sofort / Downgrade+Intervall zum nächsten Zyklus). CAS über `version`. 409 → neu laden.
  billingChangePlan: (body: ChangePlanRequest) =>
    request<BillingStatusResponse>('billing/change-plan', { method: 'POST', body: JSON.stringify(body) }),
  // Abo kündigen; `version` aus dem zuletzt gelesenen Status (CAS). 409 → neu laden + erneut.
  billingCancel: (version: number) =>
    request<BillingStatusResponse>('billing/cancel', { method: 'POST', body: JSON.stringify({ version }) }),
  // Reconcile: echten Mandatsstatus beim Anbieter abholen → robuster als nur der Webhook. Liefert den neuen Status.
  billingReconcile: () => request<BillingStatusResponse>('billing/reconcile', { method: 'POST', body: '{}' }),
  // Abo-Rechnungen (Cevio → Werkstatt): Liste + einzelner Beleg (Druck/PDF).
  aboInvoices: () => request<AboInvoiceListResponse>('billing/invoices'),
  aboInvoice: (id: number) => request<AboInvoiceDocResponse>(`billing/invoices/${id}`),
  // Kiosk-Terminals (Phase 3). Das Geräte-Token kommt bei createKioskDevice GENAU EINMAL zurück — danach
  // existiert nur noch sein Hash. Der Terminal-Pfad selbst läuft NICHT über diesen Client, sondern über
  // kiosk/kioskApi.ts (kein Session-Cookie, Geräte-Token im Header).
  kioskDevices: () => request<{ devices: KioskDeviceDTO[] }>('kiosk/devices'),
  createKioskDevice: (label: string) =>
    request<{ id: number; label: string; token: string }>('kiosk/devices', { method: 'POST', body: JSON.stringify({ label }) }),
  revokeKioskDevice: (id: number) => request<{ ok: true }>(`kiosk/devices/${id}/revoke`, { method: 'POST', body: '{}' }),
  // Terminal-PIN: der Mitarbeiter setzt sie SELBST (Passwort-Bestätigung), der Chef kann sie nur löschen.
  setMyPin: (pin: string, password: string) =>
    request<{ ok: true }>('me/pin', { method: 'PUT', body: JSON.stringify({ pin, password }) }),
  clearUserPin: (userId: number) => request<{ ok: true }>(`users/${userId}/pin`, { method: 'DELETE' }),
}

export interface KioskDeviceDTO {
  id: number
  label: string
  createdAt: string
  lastSeenAt: string | null
  revokedAt: string | null
}
